import React, { useState, useEffect } from 'react';
import { Copy, Star, Trash2, ExternalLink, FileText, Image, Link as LinkIcon, Globe, Globe2, X, Settings, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { clipboardService } from '../services/api';
import VerticalVoteButtons from './VerticalVoteButtons';
import LinkPreview from './LinkPreview';
import CommentSection from './CommentSection';

const ClipboardItem = ({ 
  item, 
  onDelete, 
  onToggleFavorite, 
  onUpdate, 
  onFavoriteSuccess, 
  onTogglePublic, 
  onEditCategory,
  onSaveCategory,
  onCancelEdit,
  onEditTitle,
  onSaveTitle,
  onCancelTitleEdit,
  editingItem,
  editCategory,
  setEditCategory,
  editingTitle,
  editTitle,
  setEditTitle,
  categories = [],
  categoriesLoading = false,
  isOwner = false,
  isPublicPage = false, 
  isCompact = false 
}) => {
  const [loading, setLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [publicLoading, setPublicLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  // Add effect to detect page refreshes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Page is about to unload/refresh
    };

    const handleUnload = () => {
      // Page is unloading/refreshing
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  const getContentTypeIcon = (contentType) => {
    switch (contentType) {
      case 'link':
        return <ExternalLink className="h-4 w-4 text-blue-500" />;
      case 'image':
        return <Image className="h-4 w-4 text-green-500" />;
      case 'long_text':
        return <FileText className="h-4 w-4 text-purple-500" />;
      case 'html':
        return <FileText className="h-4 w-4 text-orange-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getContentTypeLabel = (contentType) => {
    switch (contentType) {
      case 'link':
        return 'Link';
      case 'image':
        return 'Image';
      case 'long_text':
        return 'Long Text';
      case 'html':
        return 'HTML';
      default:
        return 'Text';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Adjust for 5-hour time difference (database appears to be 5 hours ahead)
    // Subtract 5 hours to convert to local time
    const adjustedDate = new Date(date.getTime() - (5 * 60 * 60 * 1000));
    
    const now = new Date();
    const diffInMs = now - adjustedDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else {
      return adjustedDate.toLocaleDateString();
    }
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(item.content);
      toast.success('Copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = item.content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Copied to clipboard!');
    }
  };

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setFavoriteLoading(true);
    
    try {
      const response = await clipboardService.toggleFavorite(item.id);
      
      // Handle different possible API response structures
      let isFavorite;
      if (response && response.is_favorite !== undefined) {
        // API returned explicit favorite status
        isFavorite = response.is_favorite;
      } else if (response && response.favorite !== undefined) {
        // Alternative field name
        isFavorite = response.favorite;
      } else if (response && (response.status === 'success' || response.message)) {
        // API returned success message, toggle the current state
        isFavorite = !item.is_favorite;
      } else {
        // Fallback: toggle current state
        isFavorite = !item.is_favorite;
      }
      
      onToggleFavorite(item.id, isFavorite);
      
      // Notify parent that the favorite change was successful
      if (onFavoriteSuccess) {
        onFavoriteSuccess(item.id, isFavorite);
      }
      
      toast.success(isFavorite ? 'Added to favorites!' : 'Removed from favorites!');
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite status');
    } finally {
      setFavoriteLoading(false);
    }
    
    return false; // Prevent any form submission
  };

  const handleTogglePublic = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPublicLoading(true);
    try {
      const response = await clipboardService.togglePublic(item.id);
      
      // Handle different possible API response structures
      let isPublic;
      if (response.is_public !== undefined) {
        // API returned explicit public status
        isPublic = response.is_public;
      } else if (response.public !== undefined) {
        // Alternative field name
        isPublic = response.public;
      } else if (response.status === 'success' || response.message) {
        // API returned success message, toggle the current state
        isPublic = !(item.is_public || false);
      } else {
        // Fallback: toggle current state
        isPublic = !(item.is_public || false);
      }
      
      onTogglePublic(item.id, isPublic);
      
      toast.success(isPublic ? 'Item made public!' : 'Item made private!');
    } catch (error) {
      console.error('Toggle public error:', error);
      toast.error('Failed to update public status');
    } finally {
      setPublicLoading(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    setLoading(true);
    try {
      await clipboardService.deleteItem(item.id);
      onDelete(item.id);
      toast.success('Item deleted successfully');
    } catch (error) {
      toast.error('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (item.content_type === 'link' && item.content.match(/^https?:\/\//)) {
      return (
        <div className="space-y-3">
          <div className="text-sm">
            <a
              href={item.content}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {item.content}
            </a>
          </div>
          <LinkPreview url={item.content} />
        </div>
      );
    }
    
    if (item.content_type === 'html') {
      return (
        <div className="space-y-2">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div 
              className="html-content"
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          </div>
          <p className="text-xs text-gray-500 text-center">
            HTML Content
          </p>
        </div>
      );
    }
    
    if (item.content_type === 'image' && item.content.match(/^https?:\/\//)) {
      return (
        <div className="space-y-2">
          <div
            className={`relative group cursor-pointer transition-all duration-300 ease-in-out ${isImageExpanded ? 'z-10' : ''}`}
            onClick={() => setIsImageExpanded((prev) => !prev)}
          >
            <img
              src={item.content}
              alt="Clipboard image"
              className={`rounded-lg object-contain transition-all duration-300 ease-in-out ${isImageExpanded ? 'max-w-full max-h-[90vh] w-full shadow-2xl border-4 border-blue-200 bg-white' : 'max-w-xs max-h-48'}`}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 ease-in-out flex items-center justify-center rounded-lg pointer-events-none">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-2">
                <Image className="h-4 w-4 text-gray-700" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center select-none">
            {isImageExpanded ? 'Click to shrink' : 'Click to expand'}
          </p>
        </div>
      );
    }

    // Use 150 for compact (favorites), 500 for regular
    const charLimit = isCompact ? 150 : 500;
    const shouldShowExpand = item.content.length > charLimit;
    const displayContent = shouldShowExpand && !isExpanded 
      ? item.content.substring(0, charLimit) 
      : item.content;

    return (
      <div className="space-y-2">
        <div className="overflow-hidden transition-all duration-300 ease-in-out">
          <p className="text-gray-900 break-words whitespace-pre-wrap">
            {displayContent}
            {shouldShowExpand && !isExpanded && (
              <span className="text-gray-500">...</span>
            )}
          </p>
        </div>
        {shouldShowExpand && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 hover:underline"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div 
        className={`clipboard-item ${item.is_favorite ? 'favorite' : ''} ${isCompact ? 'compact' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {isCompact ? (
          // Compact layout for favorites column
          <div className="space-y-2">
                         {/* Title for compact layout */}
             {item.title && (
               <div className="font-medium text-sm text-gray-900 truncate">
                 {editingTitle === item.id ? (
                   <div className="flex items-center space-x-2">
                     <input
                       type="text"
                       value={editTitle}
                       onChange={(e) => setEditTitle(e.target.value)}
                       placeholder="Enter title..."
                       className="flex-1 px-2 py-1 text-sm font-medium border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                       autoFocus
                     />
                     <button
                       onClick={() => onSaveTitle(item.id)}
                       className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
                     >
                       Save
                     </button>
                     <button
                       onClick={onCancelTitleEdit}
                       className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400 transition-colors"
                     >
                       Cancel
                     </button>
                   </div>
                 ) : (
                   <div className="flex items-center space-x-2">
                     <span className="truncate">{item.title}</span>
                     {isOwner && onEditTitle && (
                       <button
                         onClick={() => onEditTitle(item)}
                         className="p-1 text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
                         title="Edit title"
                       >
                         <Settings className="w-3 h-3" />
                       </button>
                     )}
                   </div>
                 )}
               </div>
             )}
             
             {/* Add Title button for compact layout */}
             {isOwner && !item.title && onEditTitle && (
               <div>
                 {editingTitle === item.id ? (
                   <div className="flex items-center space-x-2">
                     <input
                       type="text"
                       value={editTitle}
                       onChange={(e) => setEditTitle(e.target.value)}
                       placeholder="Enter title..."
                       className="flex-1 px-2 py-1 text-sm font-medium border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                       autoFocus
                     />
                     <button
                       onClick={() => onSaveTitle(item.id)}
                       className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
                     >
                       Save
                     </button>
                     <button
                       onClick={onCancelTitleEdit}
                       className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400 transition-colors"
                     >
                       Cancel
                     </button>
                   </div>
                 ) : (
                   <button
                     onClick={() => onEditTitle(item)}
                     className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-800 transition-colors"
                     title="Add title"
                   >
                     <Plus className="w-2 h-2 mr-1" />
                     Add Title
                   </button>
                 )}
               </div>
             )}
             
             <div className="flex items-center justify-between">
               <div className="flex items-center space-x-2">
                 {getContentTypeIcon(item.content_type)}
                 <span className="text-xs text-gray-400">
                   {formatDate(item.created_at)}
                 </span>
                 {/* Category Display for compact layout */}
                 {(item.category || item.category_name) && (
                   <>
                     <span className="text-xs text-gray-400">•</span>
                     <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                       {item.category_name || item.category}
                     </span>
                   </>
                 )}
                 {/* Category Edit Button for compact layout */}
                 {isOwner && (item.category || item.category_name) && onEditCategory && (
                   <button
                     onClick={() => onEditCategory(item)}
                     className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                     title="Edit category"
                   >
                     <Settings className="w-3 h-3" />
                   </button>
                 )}
                 {/* Add Category button for compact layout */}
                 {isOwner && !item.category && !item.category_name && onEditCategory && (
                   <>
                     <span className="text-xs text-gray-400">•</span>
                     <button
                       onClick={() => onEditCategory(item)}
                       className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-800 transition-colors"
                       title="Add category"
                     >
                       <Plus className="w-2 h-2 mr-1" />
                       Add Category
                     </button>
                   </>
                 )}
               </div>
              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors duration-200"
                  title="Copy to clipboard"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={handleToggleFavorite}
                  disabled={favoriteLoading}
                  className="p-1 text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors duration-200"
                  title="Remove from favorites"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Star className={`h-3 w-3 ${favoriteLoading ? 'animate-pulse' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={handleTogglePublic}
                  disabled={publicLoading}
                  className={`p-1 rounded transition-colors duration-200 ${
                    (item.is_public || false)
                      ? 'text-green-500 hover:text-green-600 hover:bg-green-50'
                      : 'text-gray-400 hover:text-green-500 hover:bg-gray-100'
                  }`}
                  title={(item.is_public || false) ? 'Make private' : 'Make public'}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Globe className={`h-3 w-3 ${publicLoading ? 'animate-pulse' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-200"
                  title="Delete item"
                >
                  <Trash2 className={`h-3 w-3 ${loading ? 'animate-pulse' : ''}`} />
                </button>
              </div>
            </div>
            
            {/* Inline Category Editor for compact layout */}
            {editingItem === item.id && (
              <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                <div className="flex items-center space-x-2">
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select a category...</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => onSaveCategory(item.id)}
                    disabled={!editCategory || categoriesLoading}
                    className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
                         {/* Source URL for compact layout */}
             {item.source_url && (
               <div className="mb-2">
                 <span className="text-xs text-gray-500">Source: </span>
                 <a
                   href={item.source_url}
                   target="_blank"
                   rel="noopener noreferrer"
                   className="text-xs text-blue-600 hover:text-blue-800 underline break-all"
                   onClick={(e) => e.stopPropagation()}
                 >
                   {item.source_url}
                 </a>
               </div>
             )}
             
             <div className="text-sm">
               {renderContent()}
             </div>

            {item.tags && (
              <div className="flex flex-wrap gap-1">
                {item.tags.split(',').slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {tag.trim()}
                  </span>
                ))}
                {item.tags.split(',').length > 2 && (
                  <span className="text-xs text-gray-500">+{item.tags.split(',').length - 2} more</span>
                )}
              </div>
            )}
          </div>
        ) : (
          // Regular layout for main column
          <div className="flex items-start space-x-3">
            {isPublicPage && (
              <div className="flex-shrink-0 pt-1">
                <VerticalVoteButtons 
                  itemId={item.id} 
                  initialVotes={item.votes || 0}
                  onVoteChange={(itemId, action, newVotes) => {
                    // Update the item's vote count in the parent component
                    if (onUpdate) {
                      onUpdate(itemId, { votes: newVotes });
                    }
                  }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
                             {/* Title for regular layout */}
               {item.title && (
                 <div className="font-semibold text-base text-gray-900 mb-2">
                   {editingTitle === item.id ? (
                     <div className="flex items-center space-x-2">
                       <input
                         type="text"
                         value={editTitle}
                         onChange={(e) => setEditTitle(e.target.value)}
                         placeholder="Enter title..."
                         className="flex-1 px-3 py-1.5 text-base font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                         autoFocus
                       />
                       <button
                         onClick={() => onSaveTitle(item.id)}
                         className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors"
                       >
                         Save
                       </button>
                       <button
                         onClick={onCancelTitleEdit}
                         className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
                       >
                         Cancel
                       </button>
                     </div>
                   ) : (
                     <div className="flex items-center space-x-2">
                       <span>{item.title}</span>
                       {isOwner && onEditTitle && (
                         <button
                           onClick={() => onEditTitle(item)}
                           className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                           title="Edit title"
                         >
                           <Settings className="w-3 h-3" />
                         </button>
                       )}
                     </div>
                   )}
                 </div>
               )}
               
               {/* Add Title button for items without titles */}
               {isOwner && !item.title && onEditTitle && (
                 <div className="mb-2">
                   {editingTitle === item.id ? (
                     <div className="flex items-center space-x-2">
                       <input
                         type="text"
                         value={editTitle}
                         onChange={(e) => setEditTitle(e.target.value)}
                         placeholder="Enter title..."
                         className="flex-1 px-3 py-1.5 text-base font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                         autoFocus
                       />
                       <button
                         onClick={() => onSaveTitle(item.id)}
                         className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors"
                       >
                         Save
                       </button>
                       <button
                         onClick={onCancelTitleEdit}
                         className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
                       >
                         Cancel
                       </button>
                     </div>
                   ) : (
                     <button
                       onClick={() => onEditTitle(item)}
                       className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-800 transition-colors"
                       title="Add title"
                     >
                       <Plus className="w-3 h-3 mr-1" />
                       Add Title
                     </button>
                   )}
                 </div>
               )}
               
               <div className="flex items-center space-x-2 mb-2">
                 {getContentTypeIcon(item.content_type)}
                 <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                   {getContentTypeLabel(item.content_type)}
                 </span>
                 <span className="text-xs text-gray-400">
                   {formatDate(item.created_at)}
                 </span>
                 {/* Category Display */}
                 {(item.category || item.category_name) && (
                   <>
                     <span className="text-xs text-gray-400">•</span>
                     <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                       {item.category_name || item.category}
                     </span>
                   </>
                 )}
                 {/* Category Edit Button */}
                 {isOwner && (item.category || item.category_name) && onEditCategory && (
                   <button
                     onClick={() => onEditCategory(item)}
                     className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                     title="Edit category"
                   >
                     <Settings className="w-3 h-3" />
                   </button>
                 )}
                 {/* Add Category button for items without categories */}
                 {isOwner && !item.category && !item.category_name && onEditCategory && (
                   <>
                     <span className="text-xs text-gray-400">•</span>
                     <button
                       onClick={() => onEditCategory(item)}
                       className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-800 transition-colors"
                       title="Add category"
                     >
                       <Plus className="w-3 h-3 mr-1" />
                       Add Category
                     </button>
                   </>
                 )}
                 {isPublicPage && item.author_name && (
                   <span className="text-xs text-blue-600 font-medium">
                     by {item.author_name}
                   </span>
                 )}
               </div>
               
               {/* Inline Category Editor */}
               {editingItem === item.id && (
                 <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                   <div className="flex items-center space-x-2">
                     <select
                       value={editCategory}
                       onChange={(e) => setEditCategory(e.target.value)}
                       className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                       disabled={categoriesLoading}
                     >
                       <option value="">Select a category...</option>
                       {categories.map((category) => (
                         <option key={category.id} value={category.id}>
                           {category.name}
                         </option>
                       ))}
                     </select>
                     <button
                       onClick={() => onSaveCategory(item.id)}
                       disabled={!editCategory || categoriesLoading}
                       className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       Save
                     </button>
                     <button
                       onClick={onCancelEdit}
                       className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
                     >
                       Cancel
                     </button>
                   </div>
                 </div>
               )}
               
               {/* Source URL */}
               {item.source_url && (
                 <div className="mb-2">
                   <span className="text-xs text-gray-500">Source: </span>
                   <a
                     href={item.source_url}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="text-xs text-blue-600 hover:text-blue-800 underline break-all"
                     onClick={(e) => e.stopPropagation()}
                   >
                     {item.source_url}
                   </a>
                 </div>
               )}
              
              <div className="mb-3">
                {renderContent()}
              </div>

              {item.tags && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {item.tags.split(',').map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              )}

              {/* Comments Section for Public Page */}
              {isPublicPage && (
                <CommentSection 
                  itemId={item.id} 
                  initialComments={item.comments || []}
                />
              )}
            </div>

            <div className="flex items-center space-x-1 ml-4">
              <button
                type="button"
                onClick={handleCopy}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                title="Copy to clipboard"
              >
                <Copy className="h-4 w-4" />
              </button>
              
              {!isPublicPage && (
                <>
                  <button
                    type="button"
                    onClick={handleToggleFavorite}
                    disabled={favoriteLoading}
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      item.is_favorite
                        ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50'
                        : 'text-gray-400 hover:text-yellow-500 hover:bg-gray-100'
                    }`}
                    title={item.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Star className={`h-4 w-4 ${favoriteLoading ? 'animate-pulse' : ''}`} />
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleTogglePublic}
                    disabled={publicLoading}
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      (item.is_public || false)
                        ? 'text-green-500 hover:text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:text-green-500 hover:bg-gray-100'
                    }`}
                    title={(item.is_public || false) ? 'Make private' : 'Make public'}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Globe className={`h-4 w-4 ${publicLoading ? 'animate-pulse' : ''}`} />
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title="Delete item"
                  >
                    <Trash2 className={`h-4 w-4 ${loading ? 'animate-pulse' : ''}`} />
                  </button>
                </>
              )}
              
              {isPublicPage && (
                <div className="flex items-center space-x-1">
                  {(item.is_public || false) && (
                    <div className="flex items-center space-x-1">
                      <Globe2 className="h-4 w-4 text-green-500" />
                      <span className="text-xs text-green-600 font-medium">Public</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClipboardItem; 